<template>
  <user-session-base-template>
    <z-title class="login__title" tag="h1" size="medium">
      Entrar
    </z-title>

    <form @submit.prevent="login()">
      <z-input-field
        label="CPF"
        :has-error="!userForm.fields.cpf.valid"
        :error-message="userForm.fields.cpf.error"
      >
        <z-input
          v-model="userForm.fields.cpf.value"
          :has-error="!userForm.fields.cpf.valid"
          placeholder="Ex.: 987.654.321-90"
          clear-button-aria-label="Limpar"
          v-mask="'###.###.###-##'"
          alt="CPF"
          :is-disabled="validCPF"
        />
        <z-link tag="button" type="button" @click="validCPF = false" size="small" v-if="validCPF">
          Alterar CPF
        </z-link>
      </z-input-field>

      <transition name="login__bottom-section">
        <div v-show="validCPF">
          <z-input-field
            label="Senha"
            :has-error="!userForm.fields.password.valid"
            :error-message="userForm.fields.password.error"
          >
            <z-input
              v-model="userForm.fields.password.value"
              :has-error="!userForm.fields.password.valid"
              placeholder="Informe sua senha"
              type="password"
              alt="Senha"
              autocomplete="off"
            />
          </z-input-field>
          <div class="login__recover-password-section" v-if="userForm.getValue('cpfHash')">
            <z-link
              tag="router-link"
              class="login__recover-password-link"
              size="medium"
              :to="{
                name: 'forgot-password',
                params: { cpfHash: userForm.getValue('cpfHash') }
              }"
            >
              Esqueci minha senha
            </z-link>
            <z-link
              tag="router-link"
              class="login__recover-password-link"
              size="medium"
              :to="{ name: 'first-access', params: { cpfHash: userForm.getValue('cpfHash') } }"
            >
              Primeiro acesso
            </z-link>
          </div>
          <z-checkbox
            variant="light"
            v-model="userForm.fields.keepMeConnected.value"
            alt="Manter-me conectado"
            class="login__session-remember"
          >
            Manter-me conectado
          </z-checkbox>
        </div>
      </transition>

      <z-button
        :is-loading="submittingSignInForm"
        variant="primary"
        size="medium"
        behavior="block"
        type="submit"
        alt="Enviar ou continuar"
      >
        {{ enterButtonText }}
      </z-button>
    </form>
    <div class="login__social-alternative">
      <z-divider class="login__alternative-divider" />
      <z-text tag="span" size="small" color="minor" class="login__alternative-text">
        ou
      </z-text>
      <z-divider class="login__alternative-divider" />
    </div>

    <z-button-social
      class="login__social-button"
      variant="facebook"
      tag="button"
      behavior="block"
      :is-loading="submittingFacebookSignIn"
      @click="loginWithFacebook"
    />

    <z-button-social
      class="login__social-button"
      variant="google"
      tag="button"
      behavior="block"
      :is-loading="submittingGoogleSignIn"
      @click="loginWithGoogle"
    />
  </user-session-base-template>
</template>

<script>
import UserSessionBaseTemplate from 'components/user-session-base-template';
import { FormGroup } from 'utils/form';
import CurrentStudentService from 'services/CurrentStudentService';
import SessionService from 'services/SessionService';
import FacebookService from 'services/FacebookService';
import GoogleService from 'services/GoogleService';

export default {
  components: {
    UserSessionBaseTemplate,
  },
  computed: {
    enterButtonText() {
      if (this.validCPF) {
        return 'Entrar';
      }

      return 'Continuar';
    },
  },
  data() {
    return {
      userForm: new FormGroup({
        cpf: { required: true, cpf: true, valid: true },
        password: { required: true, minLength: 6, valid: true },
        cpfHash: { },
        keepMeConnected: { required: false, value: false },
      }),
      validCPF: false,
      submittingSignInForm: false,
      submittingFacebookSignIn: false,
      submittingGoogleSignIn: false,
    };
  },
  methods: {
    loginSucceded(jwt, persist) {
      SessionService.set(jwt, persist);

      return this.$router.replace({ name: 'panel' });
    },
    login() {
      if (!this.validCPF) {
        return this.validateCPF();
      }

      if (this.userForm.validateForm()) {
        this.submittingSignInForm = true;

        CurrentStudentService.signIn(this.userForm.getRawValue())
          .then(({ jwt }) => this.loginSucceded(jwt, this.userForm.getValue('keepMeConnected')))
          .catch(() => {
            this.$toast.show('danger', 'CPF ou senha inválido.', 4000);
          })
          .finally(() => {
            this.submittingSignInForm = false;
          });
      }

      return true;
    },
    validateCPF() {
      const valid = this.userForm.get('cpf').validateField();

      if (!valid) {
        this.validCPF = false;
        return false;
      }

      return CurrentStudentService.validateCPF(this.userForm.getValue('cpf'))
        .then((response) => {
          this.validCPF = true;
          this.userForm.fields.cpf.error = '';
          this.userForm.fields.cpf.valid = true;
          this.userForm.addValues({ cpfHash: response.cpf_hash });
        })
        .catch((error) => {
          this.validCPF = false;
          if (error && error.response && error.response.status === 403) {
            this.$toast.show(
              'danger',
              'Não foram encontradas mensalidades para sua conta, entre em contato conosco para mais detalhes',
              4000,
            );
            return;
          }

          this.userForm.fields.cpf.error = 'CPF não cadastrado';
          this.userForm.fields.cpf.valid = false;
        });
    },
    logInFacebook() {
      return FacebookService.init()
        .then(() => FacebookService.getLoginStatus())
        .then((response) => {
          if (response.status === 'connected') {
            return FacebookService.logout();
          }

          return true;
        })
        .then(() => FacebookService.login());
    },
    loginWithFacebook() {
      this.submittingFacebookSignIn = true;

      this.logInFacebook()
        .then(({ authResponse: { accessToken } }) => CurrentStudentService.facebookSignIn(accessToken))
        .then(({ jwt }) => this.loginSucceded(jwt, false))
        .catch((error) => this.handleFacebookFailure(error))
        .finally(() => {
          this.submittingFacebookSignIn = false;
        });
    },
    loginWithGoogle() {
      this.submittingGoogleSignIn = true;

      GoogleService.init()
        .then(() => GoogleService.login())
        .then((accessToken) => CurrentStudentService.googleSignIn(accessToken))
        .then(({ jwt }) => this.loginSucceded(jwt, false))
        .catch((error) => this.handleGoogleFailure(error))
        .finally(() => {
          this.submittingGoogleSignIn = false;
        });
    },
    handleFacebookFailure(error) {
      if (error && error.response && error.response.status === 401) {
        return this.$toast.show('danger', 'Email não cadastrado', 4000);
      }

      return this.$toast.show('danger', 'Falha ao conectar-se com o Facebook', 4000);
    },
    handleGoogleFailure(error) {
      if (error && error.response && error.response.status === 401) {
        return this.$toast.show('danger', 'Email não cadastrado', 4000);
      } if (error && error.code === 408) {
        return this.$toast.show(
          'danger',
          'A autenticação com o Google expirou, tente novamente',
          4000,
        );
      } if (error && error.code === 500) {
        return this.$toast.show(
          'danger',
          'Falha ao conectar-se com o Google, entre em contato com o suporte',
          4000,
        );
      }

      return this.$toast.show('danger', 'Falha ao conectar-se com o Google', 4000);
    },
  },
};
</script>

<style lang="scss" scoped>
.login__title {
  margin-bottom: var(--space-extra-large);
}

.login__session-remember {
  margin-bottom: var(--space-extra-large);
}

.login__bottom-section-enter-active,
.login__bottom-section-leave-active {
  transition: var(--speed) var(--easing);
}

.login__bottom-section-enter-to,
.login__bottom-section-leave {
  max-height: 100px;
  overflow: hidden;
}

.login__bottom-section-enter,
.login__bottom-section-leave-to {
  max-height: 0;
  overflow: hidden;
}

.login__social-alternative {
  display: flex;
  margin-top: var(--space-large);
  width: 100%;
}

.login__alternative-divider {
  flex: 1;
  margin: auto 0;
}

.login__alternative-text {
  margin: 0 var(--space-medium);
}

.login__social-button {
  margin-top: var(--space-large);
}

.login__recover-password-section {
  margin-bottom: var(--space-large);
}

.login__recover-password-link {
  display: block;
}
</style>
